'use client';
import {Box} from '@mui/material';
import Container from '@mui/material/Container';
import {styled} from '@mui/material/styles';
import type {FooterContent, FooterPreset, NavLink} from '@olympus-frontend/tenant-config';
import {useSiteConfig} from '@olympus-frontend/tenant-config';
import type React from 'react';
import {useTranslation} from 'react-i18next';
import {LOCALES} from '../../shared/constants/locales';
import {BrandLogo} from '../header';
import {Typography} from '../primitives/Typography';
import {buildContactRows} from './contact-href';
import {FooterBottomBar} from './FooterBottomBar';
import {FooterContactColumn} from './FooterContactColumn';
import {FooterNavLink} from './FooterNavLink';
import {FooterSocialColumn} from './FooterSocialColumn';
import {ColumnHeading, ColumnLinks} from './styles';

export interface FooterLinkColumn {
  headingKey: string;
  links: NavLink[];
}

interface FooterProps {
  content: FooterContent;
  homeHref: string;
  preset?: FooterPreset;
}

const LOGO_WIDTH = 140;
const LOGO_HEIGHT = 47;

const LOGO_HEIGHT_BY_LOCALE = {[LOCALES.ARABIC]: 50};

const WIDE_COLUMN_SPAN = '1.5fr';
const NARROW_COLUMN_SPAN = '1fr';

const FooterRoot = styled(Box)<{component?: React.ElementType}>(({theme}) => ({
  // Same layer as HeroCurtain: a pinned hero overhangs the content that follows it,
  // and without this the hero paints over the top of the footer on the home page.
  position: 'relative',
  zIndex: 1,
  background: theme.palette.colors.surface.footer.default,
  color: theme.palette.colors.text.neutral.inverse,
  paddingBlock: theme.spacing(6),
  [theme.breakpoints.down('md')]: {
    paddingBlock: theme.spacing(4),
  },
}));

const Columns = styled(Box)(({theme}) => ({
  display: 'grid',
  gridTemplateColumns: '1fr',
  '& > *:not(:first-of-type)': {
    marginTop: theme.spacing(3),
    paddingTop: theme.spacing(3),
    borderTop: `1px solid ${theme.palette.colors.border.neutral.inverse}`,
  },
  [theme.breakpoints.up('md')]: {
    gridTemplateColumns: 'var(--col-template)',
    gap: theme.spacing(10),
    '& > *:not(:first-of-type)': {marginTop: 0, paddingTop: 0, borderTop: 'none'},
  },
}));

const BrandColumn = styled(Box)(({theme}) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(2.5),
}));

export function Footer({content, homeHref, preset}: FooterProps) {
  const {t} = useTranslation();
  const {branding, contact, social = [], presets} = useSiteConfig();
  const {descriptionKey, columns, copyrightKey, contactHeadingKey, legalLinks = []} = content;
  const year = new Date().getFullYear();
  const contactRows = contact ? buildContactRows(contact, preset ?? presets.footer, t) : [];
  const hasContact = contactRows.length > 0;
  const hasSocial = social.length > 0;
  const colTemplate = [
    WIDE_COLUMN_SPAN,
    ...columns.map(() => NARROW_COLUMN_SPAN),
    ...(hasContact ? [WIDE_COLUMN_SPAN] : []),
    ...(hasSocial ? [NARROW_COLUMN_SPAN] : []),
  ].join(' ');

  return (
    <FooterRoot component="footer">
      <Container>
        <Columns style={{'--col-template': colTemplate} as React.CSSProperties}>
          <BrandColumn sx={descriptionKey ? undefined : {alignItems: 'center', justifyContent: 'center'}}>
            <BrandLogo
              href={homeHref}
              logoUrl={branding.logoUrl}
              logoUrlByLocale={branding.logoUrlByLocale}
              alt={branding.siteName}
              width={LOGO_WIDTH}
              height={LOGO_HEIGHT}
              heightByLocale={LOGO_HEIGHT_BY_LOCALE}
            />
            {descriptionKey && (
              <Typography size="body-2" weight="regular" sx={{color: theme => theme.palette.colors.text.neutral.inverseMuted}}>
                {t(descriptionKey)}
              </Typography>
            )}
          </BrandColumn>

          {columns.map((column, index) => (
            <Box key={index}>
              <ColumnHeading>{t(column.headingKey)}</ColumnHeading>
              <ColumnLinks component="ul">
                {column.links.map(link => (
                  <FooterNavLink key={link.key} item={link} />
                ))}
              </ColumnLinks>
            </Box>
          ))}

          {hasContact && <FooterContactColumn headingKey={contactHeadingKey} rows={contactRows} />}

          {hasSocial && <FooterSocialColumn social={social} />}
        </Columns>

        <FooterBottomBar copyright={t(copyrightKey, {currentYear: year})} legalLinks={legalLinks} />
      </Container>
    </FooterRoot>
  );
}

Footer.displayName = 'Footer';
